import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import React from 'react';
import { Page } from '../../components/Page/Page';
import { PricingPageRedesign } from '../../components/PricingPageRedesign/PricingPageRedesign';
import { composeGetServerSideProps } from '../../utils/page/composeGetServerSideProps';
import { addFeatureFlagsProp, withFeatureFlags } from '../../utils/page/featureFlags';
import { withCustomerLoggedInProp } from '../../utils/page/auth';
import { createExperienceApiClientWithHeaderForwarding } from '../../clients/experience-api';
import { getPricingOptions } from '../../fetchers/getPricingOptions';

type Props = {
  pricingOptions: Awaited<ReturnType<typeof getPricingOptions>>;
};

// If the pricing-options endpoint is briefly unavailable, the page must still render — an empty
// matrix degrades to "no price shown" (each component already null-guards), not a 500 page.
const FALLBACK_PRICING_OPTIONS: Props['pricingOptions'] = { options: [], discountSteps: [] };

export const getServerSideProps = composeGetServerSideProps(
  withFeatureFlags(addFeatureFlagsProp),
  withCustomerLoggedInProp,
  async (context) => {
    const experienceApiClient = createExperienceApiClientWithHeaderForwarding(context);
    const pricingOptions = await getPricingOptions(experienceApiClient).catch(
      () => FALLBACK_PRICING_OPTIONS,
    );
    return { props: { pricingOptions } };
  },
) satisfies GetServerSideProps<Props>;

const PRICING_PAGE_TITLE = 'Gousto Pricing: Recipe Box Prices in Ireland from €2 a Portion';
const PRICING_PAGE_DESCRIPTION =
  'See Gousto recipe box pricing in Ireland. Pick 2 or 4 person boxes with 2–5 recipes from €2 a portion on your first box and free delivery.';

export default function PricingPage({
  customerLoggedIn,
  features,
  pricingOptions,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <Page customerLoggedIn={customerLoggedIn} features={features}>
      <Head>
        <title>{PRICING_PAGE_TITLE}</title>
        <meta name="description" content={PRICING_PAGE_DESCRIPTION} />
      </Head>
      <PricingPageRedesign pricingOptions={pricingOptions} />
    </Page>
  );
}
